import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { 
  Star, 
  ClipboardList, 
  Target, 
  Calendar, 
  Loader2 
} from "lucide-react";
import { useToast } from "../../../components/admin/ToastContext";
import { get, post } from "../../../services/lewooApi";

const RATING_LABEL_IDS = [
  "ess.appraisals.skillTechnical",
  "ess.appraisals.skillCommunication",
  "ess.appraisals.skillLeadership",
  "ess.appraisals.skillProblemSolving",
  "ess.appraisals.skillTeamwork",
];

const STATUS_LABELS = {
  Completed: "ess.appraisals.completed",
  "In Progress": "ess.appraisals.inProgress",
  Pending: "ess.appraisals.pending",
};

const STATUS_STYLES = {
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-100",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-100",
  Pending: "bg-slate-50 text-slate-600 border-slate-100",
};

const ESSAppraisalsPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const { user } = useSelector((state) => state.auth);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [ratings, setRatings] = useState([0, 0, 0, 0, 0]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [currentAppraisalId, setCurrentAppraisalId] = useState(null);
  const [nextReviewDate, setNextReviewDate] = useState(null);
  const [apiGoals, setApiGoals] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const empRes = await get("/api/employees?limit=500");
        const allEmployees = empRes?.data || [];
        const currentEmp = allEmployees.find((e) => e.work_email === user?.email) || null;
        setEmployeeId(currentEmp?.id || currentEmp?.employee_id);

        if (currentEmp) {
          const res = await get(`/api/appraisals?employee_id=${currentEmp.id || currentEmp.employee_id}`);
          const rows = res?.data || [];
           const mapped = rows.map((r) => ({
             period: r.date ? new Date(r.date).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "—",
             type: r.name || "Performance Review",
             rating: r.final_score ? `${r.final_score}%` : "—",
             status: r.state === "done" || r.state === "APPROVED" ? "Completed" : r.state === "confirm" || r.state === "IN_PROGRESS" ? "In Progress" : "Pending",
           }));
           setReviewHistory(mapped);

           const latest = rows.find(r => r.state === "done" || r.state === "APPROVED")
             || rows[0];
            const selfEvalTarget = rows.find(r => r.state !== "done" && r.state !== "APPROVED") || latest;
            setCurrentAppraisalId((selfEvalTarget?.id ?? selfEvalTarget?.lewoo_uid) || null);
            if (selfEvalTarget?.date_close || selfEvalTarget?.date) {
              setNextReviewDate(new Date(selfEvalTarget.date_close || selfEvalTarget.date).toLocaleDateString(intl.locale, { day: "numeric", month: "short", year: "numeric" }));
            }
            if (latest && latest.id) {
              try {
                const goalsRes = await get(`/api/appraisals/${latest.id}/goals`);
                const goalsData = goalsRes?.data || [];
                setApiGoals(goalsData.map((g) => ({
                  label: g.name || g.label || "—",
                  progress: Number(g.progress) || Number(g.score) || 0,
                })));
              } catch (_) {
                setApiGoals([]);
              }
            }
        }
      } catch (_) {}
    })();
  }, [user, intl.locale]);

  const handleSubmit = async () => {
    if (!employeeId) {
      addToast(intl.formatMessage({ id: "ess.appraisals.noEmployee" }), "error");
      return;
    }
    if (!currentAppraisalId) {
      addToast(intl.formatMessage({ id: "ess.appraisals.noTarget" }), "error");
      return;
    }
    if (ratings.some((r) => r < 1)) {
      addToast(intl.formatMessage({ id: "ess.common.fillAllFields" }), "error");
      return;
    }
    setSubmitting(true);
    try {
      const avg = Number((ratings.reduce((s, r) => s + r, 0) / ratings.length).toFixed(1));
      const ratingsObj = Object.fromEntries(ratings.map((r, i) => [`competency_${i + 1}`, r]));

      await post(`/api/appraisals/${currentAppraisalId}/self-eval`, {
        rating: avg,
        ratings: ratingsObj,
        note_summary: comment.trim(),
      });

      addToast(intl.formatMessage({ id: "ess.appraisals.submitSuccess" }), "success");
      setSubmitting(false);
      setComment("");
      window.location.reload();
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.common.submitError" }), "error");
      setSubmitting(false);
    }
  };

  const toggleStar = (idx, star) => {
    const newRatings = [...ratings];
    newRatings[idx] = star;
    setRatings(newRatings);
  };

  const avgRating = (ratings.reduce((s, r) => s + r, 0) / ratings.length).toFixed(1);

  const goals = apiGoals;
  const goalsMetValue = goals.length === 0
    ? "—"
    : `${goals.reduce((s, g) => s + (g.progress >= 80 ? 1 : 0), 0)}/${goals.length}`;

  const stats = [
    { labelId: "ess.appraisals.currentRating", value: `${avgRating}/5`, sub: intl.formatMessage({ id: "ess.appraisals.periodQ2" }), icon: Star, color: "text-amber-600 bg-amber-100" },
    { labelId: "ess.appraisals.reviewsDone", value: String(reviewHistory.length), sub: intl.formatMessage({ id: "ess.appraisals.subHistory" }), icon: ClipboardList, color: "text-blue-600 bg-blue-100" },
    { labelId: "ess.appraisals.goalsMet", value: goalsMetValue, sub: intl.formatMessage({ id: "ess.appraisals.subGoals" }), icon: Target, color: "text-green-600 bg-green-100" },
    { labelId: "ess.appraisals.nextReview", value: nextReviewDate || "—", sub: intl.formatMessage({ id: "ess.appraisals.periodQ2" }), icon: Calendar, color: "text-violet-600 bg-violet-100" },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {intl.formatMessage({ id: "ess.appraisals.title" })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {intl.formatMessage({ id: "ess.appraisals.subtitle" })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, sub, icon: Icon, color }) => (
          <div key={labelId} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: labelId })}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-400 mt-1">{sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">{intl.formatMessage({ id: "ess.appraisals.selfReviewTitle" })}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{intl.formatMessage({ id: "ess.appraisals.deadline" })}</p>
              </div>
              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">{intl.formatMessage({ id: "ess.appraisals.pending" })}</span>
            </div>

            <div className="space-y-5">
              {RATING_LABEL_IDS.map((labelId, i) => (
                <div key={labelId}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-700">{intl.formatMessage({ id: labelId })}</span>
                    <span className="text-sm font-bold text-gray-900">{ratings[i]}/5</span>
                  </div>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => toggleStar(i, star)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-all ${
                          star <= ratings[i]
                            ? "bg-emerald-100 text-emerald-700 shadow-inner"
                            : "bg-gray-50 text-gray-300 hover:bg-gray-100"
                        }`}
                      >
                        <Star size={16} fill={star <= ratings[i] ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{intl.formatMessage({ id: "ess.appraisals.comments" })}</label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none"
                placeholder={intl.formatMessage({ id: "ess.appraisals.commentsPlaceholder" })}
              />
            </div>

            <div className="mt-4 flex items-center justify-end gap-3">
              {!currentAppraisalId && (
                <span className="text-xs text-amber-600">{intl.formatMessage({ id: "ess.appraisals.noTarget" })}</span>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || !currentAppraisalId}
                className="inline-flex items-center px-5 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> {intl.formatMessage({ id: "ess.common.sending" })}</> : intl.formatMessage({ id: "ess.common.submit" })}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">{intl.formatMessage({ id: "ess.appraisals.history" })}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-3 font-medium">{intl.formatMessage({ id: "ess.appraisals.colPeriod" })}</th>
                    <th className="px-4 py-3 font-medium">{intl.formatMessage({ id: "ess.appraisals.colType" })}</th>
                    <th className="px-4 py-3 font-medium">{intl.formatMessage({ id: "ess.appraisals.colRating" })}</th>
                    <th className="px-4 py-3 font-medium">{intl.formatMessage({ id: "ess.appraisals.colStatus" })}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reviewHistory.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">{intl.formatMessage({ id: "ess.appraisals.noHistory" })}</td></tr>
                  ) : (
                    reviewHistory.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50/80">
                        <td className="px-4 py-3 font-medium text-gray-900">{row.period}</td>
                        <td className="px-4 py-3 text-gray-600">{row.type}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{row.rating}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[row.status] || STATUS_STYLES.Pending}`}>
                            {intl.formatMessage({ id: STATUS_LABELS[row.status] || "ess.appraisals.pending" })}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">{intl.formatMessage({ id: "ess.appraisals.goalsTitle" })}</h3>
          {goals.length === 0 ? (
            <div className="py-10 text-center text-gray-400">{intl.formatMessage({ id: "ess.appraisals.noGoals" })}</div>
          ) : (
          <div className="space-y-4">
            {goals.map((goal) => (
              <div key={goal.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">{goal.label}</span>
                  <span className="text-xs font-semibold text-gray-500">{goal.progress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${goal.progress >= 80 ? "bg-green-500" : goal.progress >= 60 ? "bg-amber-500" : "bg-blue-500"}`}
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ESSAppraisalsPage;
